import { type ParentProps, createContext, useContext } from 'solid-js';
import { createStore, produce, reconcile } from 'solid-js/store';

import type {
    CalculatorError,
    CalculatorState,
    LoadingKey,
    Offer,
    PopupKey,
    QuestionItem,
    Utm
} from './types';

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: CalculatorState = {
    assessments: {
        total: 0,
        current_page: 0,
        max_page: 0,
        assessments: []
    },
    quote: {
        lat: null,
        lng: null,
        area: null,
        address: null,
        type: null,
        polygon: null
    },
    total: 8,
    step: 0,
    direction: 'continue',
    question: { label: 'search', input: 'address', type: 'map' },
    set: 'desktop',
    offer: null,
    loading: {
        getOffer: false,
        confirmGetOffer: false
    },
    loadingPercentage: 0,
    error: null,
    popup: {
        buildingType: false,
        invalidAddress: false,
        measureVideoTutorial: false,
        help: false,
        error: false
    },
    isDrawing: false,
    utm: {
        source: null,
        medium: null,
        campaign: null,
        content: null,
        term: null
    },
    referralCode: null
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export interface CalculatorActions {
    next: () => void;
    back: () => void;
    setQuote: <K extends keyof CalculatorState['quote']>(
        key: K,
        value: CalculatorState['quote'][K]
    ) => void;
    setQuestion: (question: QuestionItem) => void;
    setStep: (step: number) => void;
    setTotal: (total: number) => void;
    setDirection: (direction: 'continue' | 'back') => void;
    setOffer: (offer: Offer) => void;
    setLoading: (key: LoadingKey, value: boolean) => void;
    setLoadingPercentage: (percentage: number) => void;
    setError: (error: CalculatorError) => void;
    clearError: () => void;
    togglePopup: (key: PopupKey, value?: boolean) => void;
    setIsDrawing: (value: boolean) => void;
    setUtm: (utm: Partial<Utm>) => void;
    setReferralCode: (code: string | null) => void;
    setSet: (value: 'desktop' | 'mobile') => void;
    reset: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type CalculatorContextValue = [CalculatorState, CalculatorActions];

const CalculatorContext = createContext<CalculatorContextValue>();

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function CalculatorProvider(props: ParentProps) {
    const [state, setState] = createStore<CalculatorState>(
        structuredClone(initialState)
    );

    const actions: CalculatorActions = {
        next() {
            setState(
                produce((s) => {
                    s.direction = 'continue';
                    s.step = Math.min(s.step + 1, s.total);
                })
            );
        },

        back() {
            setState(
                produce((s) => {
                    s.direction = 'back';
                    s.step = Math.max(s.step - 1, 0);
                })
            );
        },

        setQuote(key, value) {
            setState('quote', key, value as never);
        },

        setQuestion(question) {
            setState('question', question);
        },

        setStep(step) {
            setState('step', step);
        },

        setTotal(total) {
            setState('total', total);
        },

        setDirection(direction) {
            setState('direction', direction);
        },

        setOffer(offer) {
            setState('offer', offer);
        },

        setLoading(key, value) {
            setState('loading', key, value);
        },

        setLoadingPercentage(percentage) {
            setState('loadingPercentage', percentage);
        },

        setError(error) {
            setState('error', error);
        },

        clearError() {
            setState('error', null);
        },

        togglePopup(key, value) {
            setState('popup', key, value ?? !state.popup[key]);
        },

        setIsDrawing(value) {
            setState('isDrawing', value);
        },

        setUtm(utm) {
            setState(
                produce((s) => {
                    Object.assign(s.utm, utm);
                })
            );
        },

        setReferralCode(code) {
            setState('referralCode', code);
        },

        setSet(value) {
            setState('set', value);
        },

        reset() {
            setState(reconcile(structuredClone(initialState)));
        }
    };

    return (
        <CalculatorContext.Provider value={[state, actions]}>
            {props.children}
        </CalculatorContext.Provider>
    );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCalculator(): CalculatorContextValue {
    const ctx = useContext(CalculatorContext);
    if (!ctx) {
        throw new Error(
            'useCalculator must be used within a <CalculatorProvider>'
        );
    }
    return ctx;
}
